import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import AttachRolesModal from "../../components/users/AttachRolesModal";
import { hasPermission } from "../../utils/uiAccessControl";
import { isAdmin, isCustomer } from "../../utils/normalizeId";
import AddTaskModal from "../../components/tasks/AddTaskModal";
import MapPickerModal from "../../components/map/MapPickerModal";
import { resendPasswordLink, updateUser, updateUserStatus } from "../../api/authApi";
import {
    Paperclip,
    Key,
    Loader,
    Check,
    PenLine,
    Power,
    ChevronDown,
    ChevronRight,
    Search,
    UserRound,
    EllipsisVertical,
} from "lucide-react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

export default function UsersTable({ data, title, onAddUser, onViewUser }) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [attachUser, setAttachUser] = useState(null);
    const [openAddTask, setOpenAddTask] = useState(false);
    const [taskPrefill, setTaskPrefill] = useState(null);
    const [resendStates, setResendStates] = useState({});
    const [statusStates, setStatusStates] = useState({});
    const [confirmStatusModal, setConfirmStatusModal] = useState(null);
    const [confirmStatusLoading, setConfirmStatusLoading] = useState(false);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [actionMenuPosition, setActionMenuPosition] = useState(null);

    const actionMenuRefs = useRef({});
    const portalMenuRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    });

    const [editUser, setEditUser] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [editError, setEditError] = useState("");
    const [editSuccess, setEditSuccess] = useState("");

    const [editForm, setEditForm] = useState({
        firstName: "",
        lastName: "",
        identityId: "",
        email: "",
        phone: "",
        role: "",
        status: "",
        location: null,
    });

    const showLocationPicker = title?.toUpperCase() !== "ADMIN";

    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return data;

        const q = searchTerm.toLowerCase();
        return data.filter((u) =>
            (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.phone || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q) ||
            (u.status || "").toLowerCase().includes(q) ||
            (u.identityId || "").toLowerCase().includes(q)
        );
    }, [searchTerm, data]);

    const totalPages = Math.ceil(filteredData.length / pageSize);
    const start = (page - 1) * pageSize;
    const paginated = filteredData.slice(start, start + pageSize);
    const avatarStyles = [
        "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
        "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
        "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300",
        "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300",
    ];

    const getAvatarStyle = (name = "") => {
        const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return avatarStyles[hash % avatarStyles.length];
    };

    useEffect(() => {
        if (page > (totalPages || 1)) {
            setPage(1);
        }
    }, [totalPages, page]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openActionMenu) return;

            const anchorRef = actionMenuRefs.current[openActionMenu];
            const clickedAnchor = anchorRef?.contains(event.target);
            const clickedPortalMenu = portalMenuRef.current?.contains(event.target);

            if (!clickedAnchor && !clickedPortalMenu) {
                setOpenActionMenu(null);
                setActionMenuPosition(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionMenu]);

    useEffect(() => {
        if (openAddTask && taskPrefill) {
            console.log("OPEN:", openAddTask);
        }
    }, [openAddTask, taskPrefill]);

    const getUserKey = (user) => user.identityId || user.email;

    const getInitials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join("") || "U";

    const isUserActive = (user) =>
        String(user.status || "").toUpperCase() === "ACTIVE";

    const handleOpenActionMenu = (rowKey, event) => {
        event.stopPropagation();

        const rect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 240;
        const spacing = 8;

        const left = Math.max(
            spacing,
            Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - spacing)
        );

        const top = rect.bottom + 8;

        if (openActionMenu === rowKey) {
            setOpenActionMenu(null);
            setActionMenuPosition(null);
            return;
        }

        setOpenActionMenu(rowKey);
        setActionMenuPosition({
            top,
            left,
            width: menuWidth,
        });
    };

    const handleResendPasswordLink = async (user, e) => {
        e?.stopPropagation?.();

        const key = getUserKey(user);

        try {
            setResendStates((prev) => ({
                ...prev,
                [key]: { loading: true, success: false },
            }));

            const result = await resendPasswordLink({
                email: user.email,
                userType: title.toUpperCase(),
            });

            if (result.success) {
                setResendStates((prev) => ({
                    ...prev,
                    [key]: { loading: false, success: true },
                }));

                setTimeout(() => {
                    setResendStates((prev) => ({
                        ...prev,
                        [key]: { loading: false, success: false },
                    }));
                }, 1500);
            } else {
                setResendStates((prev) => ({
                    ...prev,
                    [key]: { loading: false, success: false },
                }));
                alert(result.message || "Failed to resend password link");
            }
        } catch (err) {
            console.error(err);
            setResendStates((prev) => ({
                ...prev,
                [key]: { loading: false, success: false },
            }));
            alert("Error sending password link");
        } finally {
            setOpenActionMenu(null);
            setActionMenuPosition(null);
        }
    };

    const handleToggleStatus = (user, e) => {
        e?.stopPropagation?.();

        if (!user?.identityId) {
            alert("User identityId is missing");
            return;
        }

        const fromStatus = isUserActive(user) ? "ACTIVE" : "INACTIVE";
        const toStatus = fromStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";

        setConfirmStatusModal({
            user,
            fromStatus,
            toStatus,
        });
        setOpenActionMenu(null);
        setActionMenuPosition(null);
    };

    const handleConfirmToggleStatus = async () => {
        if (!confirmStatusModal?.user?.identityId) {
            alert("User identityId is missing");
            return;
        }

        const user = confirmStatusModal.user;
        const key = getUserKey(user);

        try {
            setConfirmStatusLoading(true);
            setStatusStates((prev) => ({
                ...prev,
                [key]: { loading: true },
            }));

            const result = await updateUserStatus({
                identityId: user.identityId,
                fromStatus: confirmStatusModal.fromStatus,
                toStatus: confirmStatusModal.toStatus,
            });

            if (result.success) {
                setStatusStates((prev) => ({
                    ...prev,
                    [key]: { loading: false, success: true },
                }));

                setTimeout(() => {
                    setConfirmStatusLoading(false);
                    setConfirmStatusModal(null);
                    window.location.reload();
                }, 800);
            } else {
                setStatusStates((prev) => ({
                    ...prev,
                    [key]: { loading: false, success: false },
                }));
                setConfirmStatusLoading(false);
                alert(result.message || "Failed to update user status");
            }
        } catch (err) {
            console.error(err);
            setStatusStates((prev) => ({
                ...prev,
                [key]: { loading: false, success: false },
            }));
            setConfirmStatusLoading(false);
            alert("Error updating user status");
        }
    };

    const handleOpenEdit = (user, e) => {
        e?.stopPropagation?.();

        const fullName = (user.name || "").trim();
        const nameParts = fullName.split(/\s+/).filter(Boolean);
        const firstName = user.firstName || nameParts[0] || "";
        const lastName = user.lastName || nameParts.slice(1).join(" ") || "";

        const fetchedLocation =
            user.location &&
                user.location.lat !== undefined &&
                user.location.lng !== undefined
                ? {
                    lat: Number(user.location.lat),
                    lng: Number(user.location.lng),
                    locationName: user.location.locationName,
                }
                : null;

        setEditError("");
        setEditSuccess("");
        setEditUser(user);
        setEditForm({
            firstName,
            lastName,
            identityId: user.identityId || "",
            email: user.email || "",
            phone: user.phone || user.phoneNumber || "",
            role: user.role || user.userType || "",
            status: user.status || "",
            location: fetchedLocation,
        });
        setOpenActionMenu(null);
        setActionMenuPosition(null);
    };

    const handleCloseEdit = () => {
        setEditUser(null);
        setShowMap(false);
        setSavingEdit(false);
        setEditError("");
        setEditSuccess("");
        setEditForm({
            firstName: "",
            lastName: "",
            identityId: "",
            email: "",
            phone: "",
            role: "",
            status: "",
            location: null,
        });

        setTimeout(() => {
            window.location.reload();
        }, 1200);
    };

    const handleSaveEdit = async () => {
        if (!editUser?.identityId) {
            setEditError("User identityId is missing");
            return;
        }

        setSavingEdit(true);
        setEditError("");
        setEditSuccess("");

        const payload = {
            identityId: editUser.identityId,
            firstName: editForm.firstName,
            lastName: editForm.lastName,
            ...(showLocationPicker && editForm.location
                ? {
                    location: {
                        lat: String(editForm.location.lat),
                        lng: String(editForm.location.lng),
                    },
                }
                : {}),
        };

        const result = await updateUser(payload);

        if (result.success) {
            setEditSuccess(result.message || "User Updated Successfully");

            setTimeout(() => {
                handleCloseEdit();
            }, 1200);
        } else {
            setEditError(result.message || "Failed to update user");
        }

        setSavingEdit(false);
    };

    const editMapCenter =
        editForm.location &&
            editForm.location.lat !== undefined &&
            editForm.location.lng !== undefined
            ? {
                lat: Number(editForm.location.lat),
                lng: Number(editForm.location.lng),
            }
            : null;

    const renderStatusPill = (user) => {
        const active = isUserActive(user);

        return (
            <span
                className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${active
                        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-400"
                        : "border-red-200 bg-red-50 text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400"
                    }`}
            >
                <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-green-500" : "bg-red-500"}`} />
                {active ? "Active" : "Inactive"}
            </span>
        );
    };

    const MenuAction = ({ icon, label, onClick, loading = false, tone = "default" }) => {
        const toneMap = {
            default: "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800",
            danger: "text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30",
            success: "text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30",
            accent: "text-primary hover:bg-primary/5 dark:text-primary dark:hover:bg-primary/10",
            warning: "text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30",
        };

        return (
            <button
                onClick={onClick}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${toneMap[tone]}`}
            >
                {loading ? <Loader className="h-4 w-4 animate-spin" /> : icon}
                <span>{label}</span>
            </button>
        );
    };

    const EnterpriseModal = ({
        open,
        onClose,
        title,
        subtitle,
        icon,
        children,
        footer,
        size = "max-w-2xl",
    }) => {
        if (!open) return null;

        return createPortal(
            <div
                className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-md"
                onClick={onClose}
            >
                <div
                    className={`w-full ${size} overflow-hidden rounded-3xl border border-white/10 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.28)] dark:border-gray-800 dark:bg-gray-950`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="border-b border-gray-200/80 bg-gradient-to-r from-gray-50 to-white px-6 py-5 dark:border-gray-800 dark:from-gray-950 dark:to-gray-950">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                                    {icon}
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
                                        {title}
                                    </h3>
                                    {subtitle && (
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            {subtitle}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={onClose}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[75vh] overflow-y-auto px-6 py-6">
                        {children}
                    </div>

                    <div className="border-t border-gray-200 bg-gray-50/80 px-6 py-4 dark:border-gray-800 dark:bg-gray-900/60">
                        {footer}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100">
            <div className="border-b border-gray-200 px-6 py-5 dark:border-gray-800">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
                                {title}
                            </h2>

                            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                                {filteredData.length} records
                            </span>
                        </div>

                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Manage users, credentials, status changes, and role assignments.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                        <div className="relative w-full lg:w-[360px]">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone, role, ID..."
                                className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                    setExpandedRow(null);
                                }}
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    Rows
                                </label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            {hasPermission(`${title.toUpperCase()}_CREATE`) && (
                                <button
                                    onClick={onAddUser}
                                    className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                                >
                                    Add {title}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px]">
                    <thead className="bg-gray-50/90 dark:bg-gray-900/80">
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                            <th className="w-12 px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"></th>
                            <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                User
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                Identity ID
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                Contact
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                Status
                            </th>
                            <th className="w-20 px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                        {paginated.map((user, index) => {
                            const rowKey = getUserKey(user) || index;

                            return (
                                <React.Fragment key={rowKey}>
                                    <tr
                                        className="group cursor-pointer bg-white transition hover:bg-gray-50/70 dark:bg-gray-950 dark:hover:bg-gray-900/70"
                                        onClick={() =>
                                            setExpandedRow(expandedRow === rowKey ? null : rowKey)
                                        }
                                    >
                                        <td className="px-6 py-4 align-top">
                                            <div className="flex items-center justify-center pt-1 text-gray-400 transition group-hover:text-gray-600 dark:group-hover:text-gray-300">
                                                {expandedRow === rowKey ? (
                                                    <ChevronDown className="h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4" />
                                                )}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 align-top">
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-s font-bold ${getAvatarStyle(user.name)}`}
                                                >
                                                    {user.name ? getInitials(user.name) : <UserRound className="h-4 w-4" />}
                                                </div>

                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                        {user.name || "Unnamed User"}
                                                    </div>
                                                    <div className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                                                        {user.role || "No role assigned"}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 align-top">
                                            <div className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                                {user.identityId || "—"}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 align-top">
                                            <div className="space-y-1">
                                                <div className="max-w-[250px] truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {user.email || "—"}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {user.phone || "—"}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 align-top">
                                            {renderStatusPill(user)}
                                        </td>

                                        <td className="px-6 py-4 align-top text-right">
                                            {hasPermission("ADMIN_EDIT") ? (
                                                <div
                                                    ref={(el) => {
                                                        actionMenuRefs.current[rowKey] = el;
                                                    }}
                                                    className="relative inline-block"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        onClick={(e) => handleOpenActionMenu(rowKey, e)}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                                                        title="More actions"
                                                    >
                                                        <EllipsisVertical className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                                            )}
                                        </td>
                                    </tr>

                                    {expandedRow === rowKey && (
                                        <tr className="bg-gray-100/90 dark:bg-gray-900">
                                            <td colSpan={6} className="px-6 py-0">
                                                <div className="py-5">
                                                    <div className="rounded-2xl border border-gray-300 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-950">
                                                        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr,0.95fr]">
                                                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-900/80">
                                                                <div className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                                                                    User Details
                                                                </div>

                                                                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 text-sm">
                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Identity ID</div>
                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                            {user.identityId || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Full Name</div>
                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                            {user.name || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Email</div>
                                                                        <div className="mt-1 break-all font-medium text-gray-900 dark:text-white">
                                                                            {user.email || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Phone</div>
                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                            {user.phone || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Role</div>
                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                            {user.role || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <div className="text-gray-500 dark:text-gray-400">Status</div>
                                                                        <div className="mt-1">{renderStatusPill(user)}</div>
                                                                    </div>
                                                                </div>

                                                                {hasPermission("ADMIN_EDIT") && isAdmin(user) && (
                                                                    <div className="mt-5">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setAttachUser(user);
                                                                            }}
                                                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800"
                                                                        >
                                                                            <Paperclip className="h-4 w-4 text-primary" />
                                                                            Attach Roles
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-900/80">
                                                                <div className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                                                                    Tractors
                                                                </div>

                                                                {user.tractors?.length > 0 ? (
                                                                    <div className="space-y-3">
                                                                        {user.tractors.map((t, ti) => (
                                                                            <div
                                                                                key={ti}
                                                                                className="rounded-xl border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-950"
                                                                            >
                                                                                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                                                                    <div>
                                                                                        <div className="text-gray-500 dark:text-gray-400">Model</div>
                                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                                            {t.model || "—"}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div>
                                                                                        <div className="text-gray-500 dark:text-gray-400">Serial</div>
                                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                                            {t.serial || "—"}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div>
                                                                                        <div className="text-gray-500 dark:text-gray-400">Year</div>
                                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                                            {t.year || "—"}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div>
                                                                                        <div className="text-gray-500 dark:text-gray-400">Engine</div>
                                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                                            {t.engineType || "—"}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div>
                                                                                        <div className="text-gray-500 dark:text-gray-400">Horse Power</div>
                                                                                        <div className="mt-1 font-medium text-gray-900 dark:text-white">
                                                                                            {t.horsePower || "—"}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {hasPermission("TASK_CREATE") && isCustomer(user) && (
                                                                                    <div className="mt-4">
                                                                                        <button
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setTaskPrefill({
                                                                                                    customer: user,
                                                                                                    tractor: t,
                                                                                                });
                                                                                                setOpenAddTask(true);
                                                                                            }}
                                                                                            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800"
                                                                                        >
                                                                                            Create Task
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400">
                                                                        No tractors associated with this user.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {filteredData.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-6 py-14 text-center">
                                    <div className="mx-auto max-w-md">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            No users found
                                        </div>
                                        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Try adjusting your search for "{searchTerm}".
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-800">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {filteredData.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, filteredData.length)} of {filteredData.length}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                        Previous
                    </button>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                        {page} / {totalPages || 1}
                    </div>

                    <button
                        disabled={page === totalPages || totalPages === 0}
                        onClick={() => setPage(page + 1)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                        Next
                    </button>
                </div>
            </div>

            {openActionMenu &&
                actionMenuPosition &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[120]"
                        onClick={() => {
                            setOpenActionMenu(null);
                            setActionMenuPosition(null);
                        }}
                    >
                        <div
                            ref={portalMenuRef}
                            className="absolute rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900 dark:ring-white/10"
                            style={{
                                top: actionMenuPosition.top,
                                left: actionMenuPosition.left,
                                width: actionMenuPosition.width,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {(() => {
                                const selectedUser = paginated.find(
                                    (u, index) => (getUserKey(u) || index) === openActionMenu
                                );

                                if (!selectedUser) return null;

                                const resendState = resendStates[getUserKey(selectedUser)] || {};
                                const statusState = statusStates[getUserKey(selectedUser)] || {};
                                const active = isUserActive(selectedUser);

                                return (
                                    <>
                                        <div className="mb-2 border-b border-gray-100 px-3 pb-2 pt-1 dark:border-gray-800">
                                            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                                Actions
                                            </div>
                                            <div className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                                                {selectedUser.name || "Unnamed User"}
                                            </div>
                                        </div>

                                        <MenuAction
                                            onClick={(e) => handleOpenEdit(selectedUser, e)}
                                            icon={<PenLine className="h-4 w-4" />}
                                            label="Edit User"
                                        />

                                        <MenuAction
                                            onClick={(e) => handleToggleStatus(selectedUser, e)}
                                            icon={<Power className="h-4 w-4" />}
                                            label={active ? "Deactivate User" : "Activate User"}
                                            loading={statusState.loading}
                                            tone={active ? "danger" : "success"}
                                        />

                                        {isAdmin(selectedUser) && (
                                            <MenuAction
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAttachUser(selectedUser);
                                                    setOpenActionMenu(null);
                                                    setActionMenuPosition(null);
                                                }}
                                                icon={<Paperclip className="h-4 w-4" />}
                                                label="Attach Roles"
                                                tone="accent"
                                            />
                                        )}

                                        {!isCustomer(selectedUser) && (
                                            <MenuAction
                                                onClick={(e) => handleResendPasswordLink(selectedUser, e)}
                                                icon={
                                                    resendState.success ? (
                                                        <Check className="h-4 w-4" />
                                                    ) : (
                                                        <Key className="h-4 w-4" />
                                                    )
                                                }
                                                label={
                                                    resendState.loading
                                                        ? "Sending Reset Link..."
                                                        : resendState.success
                                                            ? "Link Sent"
                                                            : "Send Reset Link"
                                                }
                                                loading={resendState.loading}
                                                tone={resendState.success ? "success" : "warning"}
                                            />
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>,
                    document.body
                )}

            <AttachRolesModal
                user={attachUser}
                onClose={() => setAttachUser(null)}
                onSuccess={() => setAttachUser(null)}
            />

            {openAddTask && taskPrefill && (
                <AddTaskModal
                    open={openAddTask}
                    onClose={() => {
                        setOpenAddTask(false);
                        setTaskPrefill(null);
                    }}
                    prefillCustomer={taskPrefill.customer}
                    prefillTractor={taskPrefill.tractor}
                />
            )}

            <EnterpriseModal
                open={editUser && !showMap}
                onClose={handleCloseEdit}
                title="Edit User"
                subtitle="Review and update the selected user’s profile information."
                icon={<PenLine className="h-5 w-5" />}
                size="max-w-3xl"
                footer={
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={handleCloseEdit}
                            disabled={savingEdit}
                            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleSaveEdit}
                            disabled={savingEdit}
                            className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-60 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                        >
                            {savingEdit && (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-gray-900/70 dark:border-t-transparent" />
                            )}
                            {savingEdit ? "Saving Changes..." : "Save Changes"}
                        </button>
                    </div>
                }
            >
                <div className="space-y-5">
                    {editSuccess && (
                        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-400">
                            {editSuccess}
                        </div>
                    )}

                    {editError && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
                            {editError}
                        </div>
                    )}

                    <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-800 dark:bg-gray-900/50">
                        <div className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                            Basic Information
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={editForm.firstName}
                                    onChange={(e) =>
                                        setEditForm((prev) => ({
                                            ...prev,
                                            firstName: e.target.value,
                                        }))
                                    }
                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={editForm.lastName}
                                    onChange={(e) =>
                                        setEditForm((prev) => ({
                                            ...prev,
                                            lastName: e.target.value,
                                        }))
                                    }
                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Identity ID
                                </label>
                                <input
                                    type="text"
                                    value={editForm.identityId}
                                    disabled
                                    className="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={editForm.email}
                                    disabled
                                    className="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Phone
                                </label>
                                <input
                                    type="text"
                                    value={editForm.phone}
                                    disabled
                                    className="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Role
                                </label>
                                <input
                                    type="text"
                                    value={editForm.role}
                                    disabled
                                    className="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Status
                                </label>
                                <input
                                    type="text"
                                    value={editForm.status}
                                    disabled
                                    className="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-500 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                />
                            </div>
                        </div>
                    </div>

                    {showLocationPicker && (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-800 dark:bg-gray-900/50">
                            <div className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                                Location
                            </div>

                            <div
                                onClick={() => !savingEdit && setShowMap(true)}
                                className="relative cursor-pointer rounded-xl border border-gray-300 bg-white px-4 py-3 pl-10 transition hover:border-gray-400 dark:border-gray-700 dark:bg-gray-950 dark:hover:border-gray-600"
                            >
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    📍
                                </span>

                                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                                    {editForm.location
                                        ? `${Number(editForm.location.lat).toFixed(5)}, ${Number(editForm.location.lng).toFixed(5)}`
                                        : "Click to select location"}
                                </span>
                            </div>

                            <div className="mt-4">
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Current Location: {editForm.location?.locationName || "No location selected"}
                                </label>

                                {!isLoaded ? (
                                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400">
                                        Loading map…
                                    </div>
                                ) : editMapCenter ? (
                                    <GoogleMap
                                        mapContainerStyle={{
                                            width: "100%",
                                            height: "220px",
                                            borderRadius: "16px",
                                        }}
                                        center={editMapCenter}
                                        zoom={14}
                                    >
                                        <Marker position={editMapCenter} />
                                    </GoogleMap>
                                ) : (
                                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400">
                                        No saved coordinates yet. Click above to select location.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </EnterpriseModal>

            {showMap && showLocationPicker && (
                <MapPickerModal
                    onSelect={(coords) => {
                        setEditForm((prev) => ({
                            ...prev,
                            location: coords,
                        }));
                        setShowMap(false);
                    }}
                    onClose={() => setShowMap(false)}
                />
            )}

            <EnterpriseModal
                open={confirmStatusModal}
                onClose={() => !confirmStatusLoading && setConfirmStatusModal(null)}
                title="Confirm Status Change"
                subtitle="Review the change carefully before applying it."
                icon={<Power className="h-5 w-5" />}
                size="max-w-lg"
                footer={
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => !confirmStatusLoading && setConfirmStatusModal(null)}
                            disabled={confirmStatusLoading}
                            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleConfirmToggleStatus}
                            disabled={confirmStatusLoading}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-60 ${confirmStatusModal?.toStatus === "ACTIVE"
                                    ? "bg-green-600 hover:bg-green-700"
                                    : "bg-red-600 hover:bg-red-700"
                                }`}
                        >
                            {confirmStatusLoading && (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                            )}
                            {confirmStatusLoading ? "Processing..." : "Confirm Change"}
                        </button>
                    </div>
                }
            >
                {confirmStatusModal && (
                    <div className="space-y-5">
                        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                            You are about to update this user’s account status. This action affects access immediately.
                        </div>

                        <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-800 dark:bg-gray-900/50">
                            <div className="grid grid-cols-1 gap-4 text-sm">
                                <div className="flex items-center justify-between gap-4 border-b border-gray-200 pb-3 dark:border-gray-800">
                                    <span className="text-gray-500 dark:text-gray-400">Name</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {confirmStatusModal.user.name}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between gap-4 border-b border-gray-200 pb-3 dark:border-gray-800">
                                    <span className="text-gray-500 dark:text-gray-400">Identity ID</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {confirmStatusModal.user.identityId}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between gap-4 border-b border-gray-200 pb-3 dark:border-gray-800">
                                    <span className="text-gray-500 dark:text-gray-400">Current Status</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {confirmStatusModal.fromStatus}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500 dark:text-gray-400">New Status</span>
                                    <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${confirmStatusModal.toStatus === "ACTIVE"
                                                ? "bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400"
                                                : "bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400"
                                            }`}
                                    >
                                        {confirmStatusModal.toStatus}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </EnterpriseModal>
        </div>
    );
}